import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import batchService from '../services/batchService';
import productService from '../services/productService';
import BatchesTable from '../components/BatchesTable';
import '../styles/batches.css';

const BatchesPage = () => {
  const [batches, setBatches] = useState([]);
  const [products, setProducts] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [productId, setProductId] = useState('');
  const [expiryBefore, setExpiryBefore] = useState('');

  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadProducts = useCallback(async () => {
    setProductsLoading(true);

    try {
      const result = await productService.getProducts({
        page: 1,
        limit: 100,
      });

      setProducts(result.products || []);
    } catch (err) {
      setError(
        err.response?.data?.message || 'Unable to load products for filtering.'
      );
    } finally {
      setProductsLoading(false);
    }
  }, []);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (search.trim()) params.search = search.trim();
      if (productId) params.product_id = productId;
      if (expiryBefore) params.expiry_before = expiryBefore;

      const result = await batchService.getBatches(params);

      setBatches(result.batches || []);

      setPagination((previous) => ({
        ...previous,
        ...(result.pagination || {}),
      }));
    } catch (err) {
      setBatches([]);
      setError(
        err.response?.data?.message || 'Unable to load batches.'
      );
    } finally {
      setLoading(false);
    }
  }, [
    pagination.page,
    pagination.limit,
    search,
    productId,
    expiryBefore,
  ]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const handleSearch = (event) => {
    event.preventDefault();
    setPagination((previous) => ({ ...previous, page: 1 }));
    setSearch(searchInput);
  };

  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
    setPagination((previous) => ({ ...previous, page: 1 }));
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setProductId('');
    setExpiryBefore('');
    setPagination((previous) => ({ ...previous, page: 1 }));
  };

  return (
    <section className="batches-page">
      <div className="batches-page-header">
        <div>
          <h1>Batches</h1>
          <p>
            Track received quantities, batch pricing, expiry dates, and
            remaining inventory for your firm.
          </p>
        </div>

        <Link to="/user/batches/create" className="batch-primary-button">
          + Add Batch
        </Link>
      </div>

      {error && (
        <div className="batch-alert batch-alert-error" role="alert">
          {error}
          <button type="button" onClick={loadBatches}>
            Retry
          </button>
        </div>
      )}

      <form className="batches-toolbar" onSubmit={handleSearch}>
        <input
          type="search"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search batch number, product, or SKU..."
          aria-label="Search batches"
        />

        <button type="submit" className="batch-secondary-button">
          Search
        </button>

        <select
          value={productId}
          onChange={handleFilterChange(setProductId)}
          aria-label="Filter by product"
          disabled={productsLoading}
        >
          <option value="">All products</option>
          {products.map((product) => (
            <option key={product.id} value={product.id}>
              {product.product_name} ({product.sku})
            </option>
          ))}
        </select>

        <label className="batch-expiry-filter">
          Expiring on or before
          <input
            type="date"
            value={expiryBefore}
            onChange={handleFilterChange(setExpiryBefore)}
          />
        </label>

        <button
          type="button"
          className="batch-secondary-button"
          onClick={clearFilters}
        >
          Clear
        </button>
      </form>

      <div className="batches-table-card">
        <div className="batches-table-heading">
          <div>
            <h2>Batch Inventory</h2>
            <p>Batch-level stock and pricing details.</p>
          </div>

          <span>{pagination.total} total batches</span>
        </div>

        <BatchesTable batches={batches} loading={loading} />

        <div className="batches-pagination">
          <span>
            Page {pagination.page} of {Math.max(pagination.totalPages, 1)}
          </span>

          <div>
            <button
              type="button"
              disabled={loading || pagination.page <= 1}
              onClick={() =>
                setPagination((previous) => ({
                  ...previous,
                  page: previous.page - 1,
                }))
              }
            >
              Previous
            </button>

            <button
              type="button"
              disabled={
                loading ||
                pagination.page >= pagination.totalPages
              }
              onClick={() =>
                setPagination((previous) => ({
                  ...previous,
                  page: previous.page + 1,
                }))
              }
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BatchesPage;